import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import subscriptionService from '../services/subscriptionService';
import {
  CheckCircle2,
  Crown,
  Loader2,
  ArrowRight,
  Sparkles
} from 'lucide-react';

const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId && token) {
      checkPaymentStatus();
    }
  }, [sessionId, token]);

  const checkPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const result = await subscriptionService.checkPaymentStatus(token, sessionId);

      if (result.payment_status === 'paid') {
        setStatus('success');
      } else if (result.status === 'expired') {
        setStatus('expired');
      } else {
        // Keep polling
        setAttempts(prev => prev + 1);
        setTimeout(checkPaymentStatus, pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
      setAttempts(prev => prev + 1);
      setTimeout(checkPaymentStatus, pollInterval);
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen mesh-gradient flex items-center justify-center p-4">
        <div className="bg-card border border-border rounded-2xl p-8 max-w-md w-full text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Verificando pago...</h2>
          <p className="text-slate-400">Por favor espera mientras confirmamos tu suscripción</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen mesh-gradient flex items-center justify-center p-4">
        <div className="bg-card border border-border rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-white" />
          </div>
          
          <h1 className="text-2xl font-bold font-['Outfit'] mb-2">
            ¡Bienvenido a Premium!
          </h1>
          
          <p className="text-slate-400 mb-6">
            Tu suscripción está activa. Ahora tienes acceso ilimitado a todas las funciones de IA.
          </p>

          <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-center gap-2 text-yellow-400 mb-2">
              <Crown className="w-5 h-5" />
              <span className="font-semibold">Beneficios Premium</span>
            </div>
            <ul className="text-sm text-slate-300 space-y-1">
              <li className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-yellow-400" />
                Análisis de riesgo ilimitado
              </li>
              <li className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-yellow-400" />
                Chat AI legal sin límites
              </li>
              <li className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-yellow-400" />
                Asesor de cláusulas ilimitado
              </li>
            </ul>
          </div>

          <button
            onClick={() => navigate('/dashboard')}
            className="btn-primary w-full py-3 flex items-center justify-center gap-2"
          >
            Ir al Dashboard
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // Error states
  return (
    <div className="min-h-screen mesh-gradient flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl p-8 max-w-md w-full text-center">
        <div className="w-20 h-20 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <Loader2 className="w-10 h-10 text-yellow-500" />
        </div>
        
        <h1 className="text-2xl font-bold font-['Outfit'] mb-2">
          {status === 'expired' ? 'Sesión Expirada' : 'Verificación Pendiente'}
        </h1>
        
        <p className="text-slate-400 mb-6">
          {status === 'expired' 
            ? 'La sesión de pago ha expirado. Por favor intenta nuevamente.'
            : 'No pudimos verificar el pago. Si completaste el pago, tu suscripción se activará pronto.'
          }
        </p>

        <div className="space-y-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="btn-primary w-full py-3"
          >
            Ir al Dashboard
          </button>
          <button
            onClick={() => window.location.reload()}
            className="w-full py-2 text-slate-400 hover:text-white transition-colors text-sm"
          >
            Reintentar verificación
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;
